import React, { useState } from 'react';
import { ACTIVE_GREEN, ACTIVE_GREEN_BG } from '../../../constants/statusColors';
import {
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Box,
  Button,
  IconButton,
} from '@mui/material';
import {
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Schedule as ScheduleIcon,
  EventOutlined as EventIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import ScheduleMeetingModal from '../../../components/committees/ScheduleMeetingModal';
import { csAdminApi } from '../../../services/api';
import DeleteConfirmDialog from '../../../components/common/DeleteConfirmDialog';
import { formatDisplayDate, getMeetingDisplayStatus } from '../../../utils/apiMappers';

const MeetingsTab = ({ committee, onRefresh }) => {
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [meetingToDelete, setMeetingToDelete] = useState(null);

  const handleScheduleMeeting = () => {
    setSelectedMeeting(null);
    setShowScheduleModal(true);
  };

  const handleEditMeeting = (meeting) => {
    setSelectedMeeting(meeting);
    setShowScheduleModal(true);
  };

  const handleDeleteMeeting = (meeting) => {
    setMeetingToDelete(meeting);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    try {
      if (committee?.id && meetingToDelete?.id) {
        await csAdminApi.deleteCommitteeMeeting(committee.id, meetingToDelete.id);
      }
    } catch (err) {
      console.error('Failed to delete meeting:', err);
    } finally {
      setShowDeleteDialog(false);
      setMeetingToDelete(null);
      if (onRefresh) onRefresh();
    }
  };

  const handleMeetingScheduled = () => {
    if (onRefresh) onRefresh();
  };
  const getStatusIcon = (status) => {
    switch (status) {
      case 'COMPLETED':
        return <CheckCircleIcon fontSize="small" sx={{ color: ACTIVE_GREEN }} />;
      case 'SCHEDULED':
        return <ScheduleIcon fontSize="small" sx={{ color: '#2196F3' }} />;
      case 'PAST DUE':
        return <ScheduleIcon fontSize="small" sx={{ color: '#E65100' }} />;
      case 'CANCELLED':
        return <CancelIcon fontSize="small" sx={{ color: '#F44336' }} />;
      default:
        return null;
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Meetings</Typography>
        <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={handleScheduleMeeting}>
          Schedule Meeting
        </Button>
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="center">Attendees</TableCell>
              <TableCell align="center">Quorum Met</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {committee.meetings && committee.meetings.length > 0 ? (
              committee.meetings.map((meeting) => {
                const displayStatus = getMeetingDisplayStatus(meeting);
                return (
                <TableRow key={meeting.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '8px',
                          bgcolor: '#E3F2FD',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <EventIcon sx={{ fontSize: 24, color: '#1976D2' }} />
                      </Box>
                      <Typography variant="body2" fontWeight={500}>
                        {formatDisplayDate(meeting.date)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{meeting.type}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getStatusIcon(displayStatus)}
                      <Typography variant="body2">{displayStatus}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    {meeting.attendees != null ? meeting.attendees : '—'}
                  </TableCell>
                  <TableCell align="center">
                    {meeting.quorumMet == null ? (
                      <Typography variant="body2" color="textSecondary">—</Typography>
                    ) : meeting.quorumMet ? (
                      <Chip
                        label="Yes"
                        size="small"
                        sx={{
                          bgcolor: ACTIVE_GREEN_BG,
            color: ACTIVE_GREEN,
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          height: 24,
                          borderRadius: '12px',
                        }}
                      />
                    ) : (
                      <Chip
                        label="No"
                        size="small"
                        sx={{
                          bgcolor: '#F5F5F5',
                          color: '#757575',
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          height: 24,
                          borderRadius: '12px',
                        }}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <IconButton size="small" onClick={() => handleEditMeeting(meeting)} title="Edit">
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        onClick={() => handleDeleteMeeting(meeting)} 
                        title="Delete"
                        sx={{ color: 'error.main' }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="textSecondary" sx={{ py: 3 }}>
                    No meetings scheduled yet
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Schedule Meeting Modal */}
      <ScheduleMeetingModal
        open={showScheduleModal}
        onClose={() => {
          setShowScheduleModal(false);
          setSelectedMeeting(null);
        }}
        onSchedule={handleMeetingScheduled}
        committeeId={committee?.id}
        committeeName={committee?.name}
        members={committee?.members || []}
        editMeeting={selectedMeeting}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={showDeleteDialog}
        onClose={() => {
          setShowDeleteDialog(false);
          setMeetingToDelete(null);
        }}
        onConfirm={confirmDelete}
        title="Cancel Meeting"
        message={`Are you sure you want to cancel the meeting scheduled for ${meetingToDelete ? new Date(meetingToDelete.date).toLocaleDateString('en-GB') : ''}?`}
      />
    </Paper>
  );
};

export default MeetingsTab;
